import "./App.css";
import React, { lazy, useEffect, useState } from "react";
import { createBrowserRouter, Link, RouterProvider } from "react-router-dom";
import Error404 from "./pages/404Error/404Error";

import { useDispatch, useSelector } from "react-redux";
import AppToaster from "./components/ui/AppToaster";
import {
  updateProfile,
  getProfile,
} from "./redux/features/customer/customerActions";
import { logoutUser, refreshToken } from "./redux/features/auth/loginActions";
import { saveToLocalStorage } from "./common/utils/saveToLocal";
import { setGeoIP } from "./redux/features/common/commonSlice";
import Layout from "./layout/Layout";
import routes from "./routes";
import RIL from "./pages/RIL/RIL";
import { logout } from "./common/utils/logout";
import { clearCustomer } from "./redux/features/customer/customerSlice";
import { clearLogin } from "./redux/features/auth/loginSlice";
import { clearSignup } from "./redux/features/auth/signupSlice";
import customer from "./routes/customer";
import serviceOneRoutes from "./routes/service-one";

import { updateUserStatus } from "./redux/features/customer/customerActions";
import LazyLoading from "./common/components/LazyLoading/LazyLoading";
import GetSupportButton from "./common/components/GetSupportButton";
import { getSingleorderdata } from "./redux/features/dashBoard/dashBoardActions";
import CustomerLayout from "./layout/CustomerLayout";
import ServicesLayoutOne from "./layout/ServiceLayoutOne";
import { getStatesServices } from "./redux/features/documentService/documentServiceActions";
import { getLakesAndPonds } from "./redux/features/property/propertyActions";
import { setUserlocation } from "./redux/features/mapSlice";
import LogoLoader from "./common/components/Loaders/LogoLoader";
import DynamicServiceResolver from "./routes/DynamicServiceResolver";
import { useAllServiesQuery } from "./redux/features/servicesQuery";
import { setServices } from "./redux/features/services/servicesSlice";
import { loadMessages } from "./common/utils/messages";
const RedirectPage = lazy(() => import("./pages/RedirectPage/RedirectPage"));
const GetApp = lazy(() => import("./pages/GetApp/GetApp"));
// Resident pages opened from a QR scan / a WhatsApp button — full-screen, no navbar.
const JoinForm = lazy(() => import("./pages/Resident/JoinForm"));
const PayLink = lazy(() => import("./pages/Resident/PayLink"));
const ResidentApp = lazy(() => import("./pages/Resident/ResidentApp"));

const router = createBrowserRouter([
  // Must be before Layout's ":serviceSlug/*", otherwise /app/orders is treated as a "service" slug.
  {
    path: "app",
    element: (
      <React.Suspense fallback={<LogoLoader />}>
        <RedirectPage />
      </React.Suspense>
    ),
    errorElement: (
      <LazyLoading>
        <Error404 />
      </LazyLoading>
    ),
  },
  {
    path: "app/*",
    element: (
      <React.Suspense fallback={<LogoLoader />}>
        <RedirectPage />
      </React.Suspense>
    ),
    errorElement: (
      <LazyLoading>
        <Error404 />
      </LazyLoading>
    ),
  },
  {
    element: <Layout />,
    errorElement: (
      <LazyLoading>
        <Error404 />
      </LazyLoading>
    ),
    children: [
      ...routes,
      {
        path: ":serviceSlug/*",
        element: (
          <LazyLoading>
            <DynamicServiceResolver />
          </LazyLoading>
        ),
      },
    ],
  },
  {
    path: "order/details",
    element: (
      <LazyLoading>
        <RIL />
      </LazyLoading>
    ),
  },
  // Resident onboarding + rent: top-level (outside Layout) so they open without
  // the site navbar, and before Layout's ":serviceSlug/*" so "join" / "pay"
  // aren't taken for service slugs.
  {
    path: "join/:code",
    element: (
      <LazyLoading>
        <JoinForm />
      </LazyLoading>
    ),
  },
  {
    path: "pay/:token",
    element: (
      <LazyLoading>
        <PayLink />
      </LazyLoading>
    ),
  },
  {
    // The resident's own app — independent of the site (own header, sign-in and
    // scroll). "View my stay" in WhatsApp and the join form land here.
    path: "me",
    element: (
      <LazyLoading>
        <ResidentApp />
      </LazyLoading>
    ),
  },
  {
    // Smart QR target: forwards phones straight to the right store.
    path: "get-app",
    element: (
      <LazyLoading>
        <GetApp />
      </LazyLoading>
    ),
  },

  {
    element: <CustomerLayout />,
    path: "customer",
    errorElement: (
      <LazyLoading>
        <Error404 />
      </LazyLoading>
    ),
    children: customer,
  },
  {
    path: "services",
    element: <ServicesLayoutOne />,
    errorElement: (
      <LazyLoading>
        <Error404 />
      </LazyLoading>
    ),
    children: serviceOneRoutes,
  },
]);

function App() {
  const dispatch = useDispatch();
  const { success, accessToken } = useSelector((state) => state.login);
  const { data: newData } = useAllServiesQuery();

  const paymentOrderId = useSelector((state) => {
    return state.dashBoard.paymentOrderId;
  });

  const customerInfo = useSelector((state) => state.customer?.Info || {});

  // GetSupportButton lives outside <RouterProvider>, so it can't call
  // useLocation. Subscribe to the data router instead and feed it the current
  // pathname (it uses this to show the FAB on the landing page only on mobile).
  const [pathname, setPathname] = useState(
    () => router.state.location.pathname,
  );
  useEffect(
    () => router.subscribe((state) => setPathname(state.location.pathname)),
    [],
  );

  // Login / order messages come from the backend; refresh the cached list.
  useEffect(() => {
    loadMessages();
  }, []);

  useEffect(() => {
    dispatch(getStatesServices()).then((res) => {
      if (res?.payload?.response?.status === 200) {
      }
    });
  }, []);

  useEffect(() => {
    if (newData?.services?.length > 0) {
      dispatch(setServices(newData.services));
    }
  }, [newData]);

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem("token"));
    const clientuserId = localStorage.getItem("clientUserId");

    if (!token) {
      if (!clientuserId) {
        const newClientUserId = Array.from({ length: 24 }, () =>
          Math.floor(Math.random() * 16).toString(16),
        ).join("");

        localStorage.setItem("clientUserId", newClientUserId);
      }
    }
    // If user is logged in as customer and we don't have profile info, fetch it
    const loginMode = localStorage.getItem("loginMode");
    if (token && loginMode === "customer") {
      if (!customerInfo || Object.keys(customerInfo).length === 0) {
        dispatch(getProfile());
      }
    }
  }, []);

  const getGeolocationByIP = async () => {
    // Skip the third-party geo lookup during the react-snap prerender pass
    // (build-time headless Chrome) — no user, and the request is blocked anyway.
    if (typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap")
      return;
    try {
      // Must be the `free.` host, not the bare freeipapi.com: the old domain
      // 302-redirects here, and a CORS request fails on a redirect response
      // that carries no Access-Control-Allow-Origin — so every lookup through
      // the bare domain silently errored and geo defaults never resolved.
      const response = await fetch("https://free.freeipapi.com/api/json");
      const data = await response.json();
      dispatch(
        setGeoIP({ latitude: data.latitude, longitude: data.longitude }),
      );
      dispatch(setUserlocation({ lat: data.latitude, lng: data.longitude }));
    } catch (error) {
      return error;
    }
  };
  useEffect(() => {
    let showPropertyPreference = localStorage.getItem("showPropertyPreference");
    if (!showPropertyPreference) {
      localStorage.setItem("showPropertyPreference", "newTab");
    }

    let showMappropertyTab = localStorage.getItem("showMappropertyTab");
    if (!showMappropertyTab) {
      localStorage.setItem("showMappropertyTab", "newTab");
    }

    // Defer the third-party IP-geolocation lookup (freeipapi.com) off the
    // first-paint critical path — nothing on load needs it immediately.
    const idle =
      typeof window !== "undefined" && window.requestIdleCallback
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 1500);
    idle(() => getGeolocationByIP());
  }, []);

  function requestUserLocation() {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          dispatch(
            setUserlocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }),
          );
          //   alert(`Location fetched successfully! Latitude: ${position.coords.latitude}, Longitude: ${position.coords.longitude}`);
        },
        (error) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              getGeolocationByIP();
              break;
            case error.POSITION_UNAVAILABLE:
              getGeolocationByIP();
              break;
            case error.TIMEOUT:
              getGeolocationByIP();
              break;
            case error.UNKNOWN_ERROR:
              getGeolocationByIP();
              break;
            default:
              getGeolocationByIP();
          }
        },
      );
    } else {
      getGeolocationByIP();
      alert("Geolocation is not supported by this browser.");
    }
  }

  let sessionObj = {
    globalTimer: {},
    eventTimer: [],
    probationTimer: {},
    elapsedTimer: [],
    isIdle: false,
    tokenExpiryTime: 0,
    currentTabId: "",
    refreshApiURL: "",
  };

  let config = {
    ELAPSED_TIMER_INTERVAL: 1,
    IDLE_TIMER: 12,
    PROBATION_TIMER: 2,
  };

  useEffect(() => {
    clearAllTimers();
    if (localStorage.getItem("token")) {
      trackUserActivity();
      validateSession();
    }
  }, [accessToken]);

  function trackUserActivity() {
    window.addEventListener("keydown", updateActiveSession);
    window.addEventListener("mousemove", updateActiveSession);
  }

  function updateActiveSession() {
    const tokenExpiryTime = localStorage.getItem("tokenExpiryTime");
    if (tokenExpiryTime) {
      if (sessionObj.isIdle) {
        resetProbation();
      }
      resetEventTimer();
      const currentTime = getCurrentTime();
      if (currentTime > tokenExpiryTime) {
        logoutCall();
      }
    } else {
      window.removeEventListener("keydown", updateActiveSession);
      window.removeEventListener("mousemove", updateActiveSession);
    }
  }

  function resetEventTimer() {
    clearTimeout(sessionObj.eventTimer);
    setEventTimer();
  }

  function setEventTimer() {
    const probationTime = convertMinsToMilliSeconds(config.PROBATION_TIMER);
    sessionObj.eventTimer.push(
      setTimeout(() => {
        sessionObj.isIdle = true;
        beginProbation();
      }, probationTime),
    );
  }

  function beginProbation() {
    const currentTime = getCurrentTime();
    saveToLocalStorage("idleTime", currentTime);
  }

  function resetProbation() {
    sessionObj.isIdle = false;
    saveToLocalStorage("idleTime", "");
  }

  function validateSession() {
    const currentTime = getCurrentTime();
    const tokenExpiryTime = localStorage.getItem("tokenExpiryTime");
    if (tokenExpiryTime) {
      sessionObj.tokenExpiryTime = tokenExpiryTime;
    }
    if (currentTime > sessionObj.tokenExpiryTime) {
      logoutCall();
    } else {
      triggerElapsedTimeCheck();
      sessionObj.isIdle = false;
      saveToLocalStorage("idleTime", "");
      resetEventTimer();
    }
  }

  function triggerElapsedTimeCheck() {
    verifyElapsedTime();
    const elapsedTimer = convertMinsToMilliSeconds(
      config.ELAPSED_TIMER_INTERVAL,
    );
    sessionObj.elapsedTimer.push(
      setInterval(() => {
        verifyElapsedTime();
      }, elapsedTimer),
    );
  }

  function verifyElapsedTime() {
    const currentTime = getCurrentTime();
    const expTime = getExpiryTime();
    if (
      localStorage.getItem("token") !== "" &&
      localStorage.getItem("token") !== "invalid" &&
      expTime - currentTime <= 90
    ) {
      extendSession();
    }
  }

  function extendSession() {
    const currentTime = getCurrentTime();
    const userIdle = isUserIdle();
    if (
      !userIdle &&
      localStorage.getItem("token") !== "" &&
      localStorage.getItem("token") !== "invalid" &&
      localStorage.getItem("refreshToken") &&
      localStorage.getItem("refreshTokenExpiryTime") > currentTime
    ) {
      dispatch(refreshToken());
    } else {
      logoutCall();
    }
  }

  function isUserIdle() {
    const currentTime = getCurrentTime();
    const idleTime = localStorage.getItem("idleTime");
    const watchTime = config.IDLE_TIMER - config.PROBATION_TIMER;
    const watchTimeInMilliSeconds = convertMinsToSeconds(watchTime);
    if (idleTime) {
      const idleTimeStart = JSON.parse(idleTime);
      const isSessionIdle =
        currentTime - idleTimeStart >= watchTimeInMilliSeconds;
      if (isSessionIdle && sessionObj.isIdle) {
        return true;
      }
      return false;
    }
    return false;
  }

  function convertMinsToMilliSeconds(time) {
    return time && time * 60 * 1000;
  }

  function convertMinsToSeconds(time) {
    return time && time * 60;
  }

  function getCurrentTime() {
    const now = new Date();
    const currentTime = Math.round(now.getTime() / 1000);
    return currentTime;
  }

  function getExpiryTime() {
    return (
      localStorage.getItem("tokenExpiryTime") || sessionObj.tokenExpiryTime || 0
    );
  }

  function logoutCall() {
    clearAllTimers();
    dispatch(logoutUser());
    logout();
    dispatch(clearCustomer());
    dispatch(clearLogin());
    dispatch(clearSignup());
    window.location = window.location.origin;
    sessionStorage.clear();
    localStorage.setItem("isPublic", true);
  }

  function clearAllTimers() {
    sessionObj.eventTimer.map((timeout) => {
      clearTimeout(timeout);
    });
    sessionObj.elapsedTimer.map((interval) => {
      clearInterval(interval);
    });
    resetProbation();
  }

  useEffect(() => {
    const loginMode = localStorage.getItem("loginMode");
    const token = localStorage.getItem("token");
    if (!token || loginMode !== "customer") return undefined;

    // Presence: mark the customer online while the tab is visible, offline the
    // moment it's hidden or closed. `visibilitychange` (→ "hidden") is the
    // reliable cross-browser signal for a tab switch/close.
    //
    // The previous version was broken: it also sent the status on `beforeunload`,
    // where `visibilityState` is still "visible", so closing the tab reported
    // "online" (the opposite of intended); async requests during unload get
    // cancelled by the browser anyway; and its cleanup ADDED an `unload`
    // listener (a leak that also re-sent "online"). Explicit logout still sends
    // "offline" from the Navbar.
    const sendStatus = () =>
      dispatch(
        updateUserStatus(
          document.visibilityState === "visible" ? "online" : "offline",
        ),
      );

    sendStatus();
    document.addEventListener("visibilitychange", sendStatus);
    return () => {
      document.removeEventListener("visibilitychange", sendStatus);
    };
  }, [dispatch]);

  return (
    <>
      <AppToaster />
      <RouterProvider router={router} />
      <GetSupportButton pathname={pathname} />
    </>
  );
}

export default App;
